import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { setPreferences, setUserStamps } from '@/features/user/userSlice';
import KeyCloakService from '@/security/KeycloakService.tsx';
import React from 'react'


const APIURL = import.meta.env.VITE_FIXED_TERM_API_URL;
const PREFERENCES_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

// Types
export interface UserPreference {
  darkMode: boolean;
  defaultPaginationValue: number;
  // Add other preference fields as needed
}

export interface UserStamp {
  id: string;
  name: string;
  signatureUUID: string;
  isDefault: boolean;
}

export interface PhoneNumber {
  id?: number;
  countryCode: string;  // Default: "+54"
  mobilePrefix: string; // Default: "9"
  areaCode: string;
  localNumber: string;
}

export interface ContactInformation {
  id?: number;
  email: string;
  phoneNumbers: PhoneNumber[];
}

export interface UserPreferencesResponse {
  uiPreference: UserPreference;
  userStamps: UserStamp[];
  contactInformation?: ContactInformation;
  // Add other preference categories as needed
}

// Function to prepare the authorization headers
const prepareHeaders = async (headers: Headers) => {
  try {
    const token = KeyCloakService.GetToken();
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-api-key', '');
    }
    return headers;
  } catch (error) {
    console.error('Error preparing headers:', error);
    return headers;
  }
};

// Create the API slice with RTK Query
export const userPreferencesApi = createApi({
  reducerPath: 'userPreferencesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${APIURL}/api/v1`,
    prepareHeaders,
  }),
  tagTypes: ['UserPreferences'],
  endpoints: (builder) => ({
    getUserPreferences: builder.query<UserPreferencesResponse, string>({
      query: (uuid) => `/user-preferences/${uuid}`,
      providesTags: ['UserPreferences'],
      async onQueryStarted(uuid, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;

          // Get current Redux state to check if user has manually changed theme
          const currentState = getState() as any;
          const currentMode = currentState.user.mode;
          const currentDarkMode = currentState.user.preferences?.darkMode;

          // Check if there was a manual theme change by comparing localStorage 'mode' with API data
          const manualMode = localStorage.getItem('mode');
          const apiDarkMode = data.uiPreference?.darkMode;

          // Only update theme preferences if there's no manual override
          if (data.uiPreference) {
            const updatedPreferences = { ...data.uiPreference };

            // If user has manually set a theme (via header button), don't override it
            if (manualMode && ((manualMode === 'dark') !== apiDarkMode)) {
              updatedPreferences.darkMode = manualMode === 'dark';
            }

            dispatch(setPreferences(updatedPreferences));
          }

          if (data.userStamps) {
            dispatch(setUserStamps(data.userStamps));
          }

          // Update localStorage with the actual current state, not API data
          const finalData = {
            ...data,
            uiPreference: {
              ...data.uiPreference,
              darkMode: manualMode ? (manualMode === 'dark') : data.uiPreference?.darkMode
            }
          };
          localStorage.setItem('userPreferences', JSON.stringify(finalData));
          localStorage.setItem('preferencesLastFetched', Date.now().toString());
        } catch (error) {
          console.error('Error fetching user preferences:', error);
        }
      },
    }),

    updateUserPreferences: builder.mutation<void, { uuid: string, preferences: Partial<UserPreferencesResponse> }>({
      query: ({ uuid, preferences }) => ({
        url: `/user-preferences/${uuid}`,
        method: 'PUT',
        body: preferences,
      }),
      invalidatesTags: ['UserPreferences'],
    }),

    getUserSignature: builder.query<string, string>({
      query: (signatureUUID) => ({
        url: `/user-preferences/signature/${signatureUUID}`,
        responseHandler: 'text',
      }),
    }),

    // Contact Information CRUD
    getContactInformation: builder.query<ContactInformation, string>({
      query: (userId) => `/user-preferences/${userId}/contact-information`,
      providesTags: ['UserPreferences'],
    }),

    createContactInformation: builder.mutation<ContactInformation, { userId: string, data: ContactInformation }>({
      query: ({ userId, data }) => ({
        url: `/user-preferences/${userId}/contact-information`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['UserPreferences'],
    }),

    updateContactInformation: builder.mutation<ContactInformation, { userId: string, data: ContactInformation }>({
      query: ({ userId, data }) => ({
        url: `/user-preferences/${userId}/contact-information`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['UserPreferences'],
    }),

    deleteContactInformation: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/user-preferences/${userId}/contact-information`,
        method: 'DELETE',
      }),
      invalidatesTags: ['UserPreferences'],
    }),
  }),
});

// Hook for accessing user preferences with automatic refresh
export const useUserPreferencesWithRefresh = (uuid: string) => {
  const { data, error, isLoading, refetch } = userPreferencesApi.endpoints.getUserPreferences.useQuery(uuid, {
    // Skip if no UUID is provided
    skip: !uuid,
    // Refetch on focus to ensure up-to-date data when the user returns to the app
    refetchOnFocus: true,
    // Refetch on reconnect to handle network interruptions
    refetchOnReconnect: true,
  });


  // Check if preferences need to be refreshed based on TTL
  React.useEffect(() => {
    const checkAndRefreshPreferences = () => {
      const lastFetched = parseInt(localStorage.getItem('preferencesLastFetched') || '0');
      const now = Date.now();

      if (now - lastFetched > PREFERENCES_TTL) {
        refetch();
      }
    };

    // Check immediately
    checkAndRefreshPreferences();

    // Set up interval to check periodically
    const intervalId = setInterval(checkAndRefreshPreferences, PREFERENCES_TTL);

    return () => clearInterval(intervalId);
  }, [uuid, refetch]);

  return { data, error, isLoading, refetch };
};

// Export hooks for easy usage
export const {
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
  useGetUserSignatureQuery,
  useGetContactInformationQuery,
  useCreateContactInformationMutation,
  useUpdateContactInformationMutation,
  useDeleteContactInformationMutation,
} = userPreferencesApi;